import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Image, ImageBackground, TextInput, Alert, StyleSheet, Platform, ActivityIndicator, Modal } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { getFirestore, doc, updateDoc, arrayUnion, getDoc, increment, deleteDoc, setDoc, serverTimestamp, query, collection, getDocs, orderBy } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { SearchBar } from '../components/SearchBar';
import { GradeCard } from '../components/GradeCard';
import { CategoryButton } from '../components/CategoryButton';
import { globalStyles } from '../styles/globalStyles';
import { venueStyles } from '../styles/venueStyles';
import { useVenues } from '../components/VenueContext';
import { DiscussionBoard } from '../components/discussion/DiscussionBoard';
import { homeStyles } from '../styles/HomeStyles';
import { VenueReportForm } from '../components/VenueReportForm';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { colors } from '../styles/theme';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { Ionicons } from '@expo/vector-icons';
import { extractAddressFromMapEmbed } from '../utils/mapEmbedUtils';
import { CustomModal } from '../components/Modal';
import { useSystemStyles } from '../hooks/useSystemStyles';
import { findRelevantManuals } from '../utils/findRelevantManuals';
import { useAdmin } from '../contexts/AdminContext';
import { usePopup } from '../contexts/PopupContext';
import * as DocumentPicker from 'expo-document-picker';

const getLetterGrade = (grade) => {
  if (!grade) return '';

  // If it's already a letter grade
  if (typeof grade === 'string') {
    const validGrade = grade.toUpperCase();
    if (['A', 'B', 'C', 'D', 'F'].includes(validGrade)) {
      return validGrade;
    }
  }

  // If it's a numeric grade
  if (typeof grade === 'number' || !isNaN(Number(grade))) {
    const numericGrade = Number(grade);
    if (numericGrade >= 4.5) return 'A';
    if (numericGrade >= 3.5) return 'B';
    if (numericGrade >= 2.5) return 'C';
    if (numericGrade >= 1.5) return 'D';
    if (numericGrade >= 1) return 'F';
  }

  return '';
};

export function VenueScreen({ route, navigation }) {
  const { venueId, fromSubmitReport = false, scrollToDiscussion = false } = route.params || {};
  const { venues } = useVenues();
  const [venueData, setVenueData] = useState(null);
  const [discussionData, setDiscussionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentView, setCurrentView] = useState('manuals');
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const [showReportForm, setShowReportForm] = useState(fromSubmitReport);
  const [isSaved, setIsSaved] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [isSaveHovered, setIsSaveHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [userType, setUserType] = useState(null);
  const [addToTourHovered, setAddToTourHovered] = useState(false);
  const [votes, setVotes] = useState({ up: 0, down: 0 });
  const [titleVotes, setTitleVotes] = useState({ up: 0, down: 0 });
  const [specsVotes, setSpecsVotes] = useState({ up: 0, down: 0 });
  const [linksVotes, setLinksVotes] = useState({ up: 0, down: 0 });
  const [userVote, setUserVote] = useState(null);
  const [userTitleVote, setUserTitleVote] = useState(null);
  const [userSpecsVote, setUserSpecsVote] = useState(null);
  const [userLinksVote, setUserLinksVote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeDiscussionTab, setActiveDiscussionTab] = useState('discussions');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingManuals, setIsEditingManuals] = useState(false);
  const [editableManuals, setEditableManuals] = useState([]);
  const [showManualSelector, setShowManualSelector] = useState(false);
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [availableManuals, setAvailableManuals] = useState([]);
  const [loadingManuals, setLoadingManuals] = useState(false);
  const [isEditingSpecSheets, setIsEditingSpecSheets] = useState(false);
  const [editableSpecSheets, setEditableSpecSheets] = useState([]);
  const [uploadingSpecIndex, setUploadingSpecIndex] = useState(null);
  const [savingManuals, setSavingManuals] = useState(false);
  const [savingSpecSheets, setSavingSpecSheets] = useState(false);

  const auth = getAuth();
  const { getStyle } = useSystemStyles();
  const { showConfirmation } = usePopup();

  const handleLocationSearch = () => {
    if (userLocation) {
      setUserLocation(null);
      return;
    }

    if (!navigator.geolocation) {
      Alert.alert('Error', 'Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();

          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });

          if (searchQuery.length > 0) {
            navigation.navigate('VenueList', {
              searchQuery: searchQuery,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              nearMe: true
            });
          }
        } catch (error) {
          console.error('Error getting location details:', error);
          Alert.alert('Error', 'Error getting your location details. Please try again.');
        }
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        Alert.alert('Error', 'Error accessing location. Please allow location access and try again.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };


  const renderManualSection = (manuals) => {
    // If admin is editing manuals, show the editor
    if (isAdmin && isEditingManuals) {
      return renderManualEditor();
    }

    if (manuals.length === 0) {
      return (
        <View>
          <Text style={venueStyles.noContentText}>No equipment manuals found</Text>
          {isAdmin && (
            <TouchableOpacity
              style={[venueStyles.manualButton, { backgroundColor: '#2980b9', marginTop: 10 }]}
              onPress={handleStartEditingManuals}
            >
              <Text style={venueStyles.manualButtonText}>+ Add Manuals (Admin)</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    return (
      <View>
        {isAdmin && (
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 8,
                borderRadius: 5,
                alignItems: 'center',
              }}
              onPress={handleStartEditingManuals}
            >
              <Text style={{ color: '#3D7FE6', fontSize: 12, fontWeight: 'bold' }}>EDIT MANUALS</Text>
            </TouchableOpacity>
          </View>
        )}
        {manuals.map((manual, index) => {
          // Ensure URL has protocol for external links
          const formatUrl = (url) => {
            if (!url) return '#';
            // If URL already has protocol, use as is
            if (url.startsWith('http://') || url.startsWith('https://')) {
              return url;
            }
            // If it looks like a domain, add https://
            if (url.includes('.') && !url.startsWith('/')) {
              return `https://${url}`;
            }
            // For relative paths, keep as is
            return url;
          };

          return (
            <TouchableOpacity
              key={index}
              style={[venueStyles.manualButton, { marginBottom: 10 }]}
              onPress={() => window.open(formatUrl(manual.url), '_blank')}
            >
              <Text style={venueStyles.manualButtonText}>{manual.name}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const renderManualEditor = () => {
    return (
      <View style={{ marginBottom: 20, backgroundColor: 'black', borderRadius: 10, padding: 10 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>Edit Manuals (Admin)</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: '#95a5a6',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center'
              }}
              onPress={handleCancelEditingManuals}
            >
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>CANCEL</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                backgroundColor: '#2980b9',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 5
              }}
              onPress={handleSaveManuals}
              disabled={savingManuals}
            >
              {savingManuals ? (
                <>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVING...</Text>
                </>
              ) : (
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVE</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {editableManuals.map((manual, index) => (
          <View
            key={index}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={manual.name}
                  onChangeText={(text) => handleUpdateManual(index, 'name', text)}
                  placeholder="Enter manual name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              <View style={{ width: "60%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>URL:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={manual.url}
                  onChangeText={(text) => handleUpdateManual(index, 'url', text)}
                  placeholder="Enter manual URL"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              <View style={{ flexDirection: 'row', height: 50, gap: 8, width: "15%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveManual(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === editableManuals.length - 1 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === editableManuals.length - 1 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveManual(index, 'down')}
                  disabled={index === editableManuals.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 6
                  }}
                  onPress={() => handleDeleteManual(index)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#2980b9',
              padding: 10,
              borderRadius: 5,
              alignItems: 'center',
              flex: 1
            }}
            onPress={handleAddManual}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>ADD NEW MANUAL</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              backgroundColor: '#27ae60',
              padding: 10,
              borderRadius: 5,
              alignItems: 'center',
              flex: 1
            }}
            onPress={() => setShowManualSelector(true)}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SELECT EXISTING MANUAL</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderSpecSheetsEditor = () => {
    return (
      <View style={{ width: '100%', marginBottom: 20, backgroundColor: 'black', borderRadius: 10, padding: 10 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>Edit Spec Sheets (Admin)</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: '#95a5a6',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center'
              }}
              onPress={handleCancelEditingSpecSheets}
            >
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>CANCEL</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                backgroundColor: '#2980b9',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 5
              }}
              onPress={handleSaveSpecSheets}
              disabled={savingSpecSheets}
            >
              {savingSpecSheets ? (
                <>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVING...</Text>
                </>
              ) : (
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVE</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {editableSpecSheets.map((spec, index) => (
          <View
            key={index}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
            <View style={{ marginBottom: 10 }}>
              <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Type:</Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'url' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'url')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>URL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'file' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'file')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>FILE</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={spec.name}
                  onChangeText={(text) => handleUpdateSpecSheet(index, 'name', text)}
                  placeholder="Enter spec sheet name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              {spec.specType === 'url' ? (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>URL:</Text>
                  <TextInput
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 5,
                      padding: 10,
                      color: '#fff',
                      fontSize: 14
                    }}
                    value={spec.url}
                    onChangeText={(text) => handleUpdateSpecSheet(index, 'url', text)}
                    placeholder="Enter spec sheet URL"
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                  />
                </View>
              ) : (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>File:</Text>
                  {spec.url ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Text style={{ color: '#4caf50', flex: 1, fontSize: 12 }} numberOfLines={1}>
                        {spec.fileName || 'File uploaded'}
                      </Text>
                      <TouchableOpacity
                        style={{
                          backgroundColor: '#27ae60',
                          padding: 8,
                          borderRadius: 5
                        }}
                        onPress={() => handleUploadSpecFile(index)}
                      >
                        <Text style={{ color: '#fff', fontSize: 12 }}>CHANGE</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={{
                        backgroundColor: '#27ae60',
                        padding: 10,
                        borderRadius: 5,
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 5
                      }}
                      onPress={() => handleUploadSpecFile(index)}
                      disabled={uploadingSpecIndex === index}
                    >
                      {uploadingSpecIndex === index ? (
                        <>
                          <ActivityIndicator size="small" color="#fff" />
                          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>UPLOADING...</Text>
                        </>
                      ) : (
                        <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>UPLOAD FILE</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              )}

              <View style={{ flexDirection: 'row', height: 50, gap: 8, width: "15%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === editableSpecSheets.length - 1 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === editableSpecSheets.length - 1 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'down')}
                  disabled={index === editableSpecSheets.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 6
                  }}
                  onPress={() => handleDeleteSpecSheet(index)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <TouchableOpacity
          style={{
            backgroundColor: '#2980b9',
            padding: 10,
            borderRadius: 5,
            alignItems: 'center',
            marginTop: 10
          }}
          onPress={handleAddSpecSheet}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold' }}>ADD NEW SPEC SHEET</Text>
        </TouchableOpacity>
      </View>
    );
  };

  useEffect(() => {
    const loadVenueData = async () => {
      try {
        // Get venue data directly from Firestore to ensure we have the latest data
        const db = getFirestore();
        const venueRef = doc(db, 'venues', venueId);
        const venueDoc = await getDoc(venueRef);

        if (venueDoc.exists()) {
          const venueData = venueDoc.data();
          console.log('Venue Data from Firestore:', venueData);
          console.log('Approved Data:', venueData.approved);

          // Parse venue_media if it exists
          if (venueData.venue_media) {
            try {
              const mediaData = JSON.parse(venueData.venue_media);
              venueData.photos = mediaData.photos;
              venueData.backgroundImage = mediaData.backgroundImage;
            } catch (error) {
              console.error('Error parsing venue media:', error);
            }
          }

          // Extract address from map embed link if available
          if (venueData.mapEmbedLink && (!venueData.fullAddress || venueData.fullAddress.trim() === '')) {
            venueData.fullAddress = extractAddressFromMapEmbed(venueData.mapEmbedLink);
          }

          setVenueData(venueData);

          // Check if venue is saved for current user
          if (auth.currentUser) {
            const userRef = doc(db, 'users', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const savedVenues = userData.savedVenues || [];
              setIsSaved(savedVenues.includes(venueId));
              setUserType(userData.userType || 'user');
              setIsAdmin(userData.userType === 'admin');
            }
          }

          if (!showReportForm) {
            // Only load discussion data if not showing report form
            setDiscussionData({
              discussions: venueData.discussions || [],
              reports: venueData.reports || []
            });
          }
        }
      } catch (error) {
        console.error('Error loading venue data:', error);
      }
      setLoading(false);
    };

    if (venueId) {
      loadVenueData();
    }
  }, [venueId, showReportForm, auth.currentUser]);

  // Load manuals when selector opens
  useEffect(() => {
    if (showManualSelector && availableManuals.length === 0) {
      loadAvailableManuals();
    }
  }, [showManualSelector]);

  useEffect(() => {
    const fetchVotes = async () => {
      if (!venueId) return;

      try {
        const db = getFirestore();
        const votesRef = doc(db, 'venues', venueId);
        const votesDoc = await getDoc(votesRef);

        if (votesDoc.exists()) {
          const data = votesDoc.data();
          setVotes({
            up: data.upVotes || 0,
            down: data.downVotes || 0
          });
          setTitleVotes({
            up: data.titleUpVotes || 0,
            down: data.titleDownVotes || 0
          });
          setSpecsVotes({
            up: data.specsUpVotes || 0,
            down: data.specsDownVotes || 0
          });
          setLinksVotes({
            up: data.linksUpVotes || 0,
            down: data.linksDownVotes || 0
          });
        }

        // Get all user's votes in parallel if they're logged in
        //   if (auth.currentUser) {
        //     const voteTypes = [
        //       { collection: 'votes', setter: setUserVote },
        //       { collection: 'titleVotes', setter: setUserTitleVote },
        //       { collection: 'specsVotes', setter: setUserSpecsVote },
        //       { collection: 'linksVotes', setter: setUserLinksVote }
        //     ];

        //     await Promise.all(
        //       voteTypes.map(async ({ collection, setter }) => {
        //         const voteRef = doc(db, 'venues', venueId, collection, auth.currentUser.uid);
        //         const voteDoc = await getDoc(voteRef);
        //         if (voteDoc.exists()) {
        //           setter(voteDoc.data().vote);
        //         }
        //       })
        //     );
        // }
        // Load user's votes from local storage
        const venueVote = localStorage.getItem(`venue_${venueId}_vote`);
        const titleVote = localStorage.getItem(`venue_${venueId}_title_vote`);
        const specsVote = localStorage.getItem(`venue_${venueId}_specs_vote`);
        const linksVote = localStorage.getItem(`venue_${venueId}_links_vote`);

        setUserVote(venueVote);
        setUserTitleVote(titleVote);
        setUserSpecsVote(specsVote);
        setUserLinksVote(linksVote);
      } catch (error) {
        console.error('Error fetching votes:', error);
      }
    };

    fetchVotes();
  }, [venueId]);

  useEffect(() => {
    if (currentView === 'media' && venueId) {
      loadDiscussionMessages();
    }
  }, [currentView, venueId]);

  useEffect(() => {
    if (activeDiscussionTab === 'uploads' && venueId && activeSection === 'discussionsAndUploads') {
      loadDiscussionMessages();
    }
  }, [activeDiscussionTab])

  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setIsMobile(width <= BREAKPOINTS.mobile);
        setIsTablet(width > BREAKPOINTS.mobile && width <= BREAKPOINTS.tablet);
        if (width <= BREAKPOINTS.mobile) {
          setActiveSection(null);
        }
      }
    };

    checkScreenSize();

    // if (typeof window !== 'undefined') {
    //   window.addEventListener('resize', checkScreenSize);
    //   return () => window.removeEventListener('resize', checkScreenSize);
    // }
  }, []);

  // Handle scrollToDiscussion parameter - open Community section with discussions tab
  useEffect(() => {
    if (scrollToDiscussion && !loading) {
      // Set the active section to discussions and uploads (Community section)
      setActiveSection('discussionsAndUploads');
      // Set the discussion tab to 'discussions' (DiscussionBoard)
      setActiveDiscussionTab('discussions');

      // Scroll to the Community section if possible
      setTimeout(() => {
        // Try to find the element by a ref or id
        const communitySection = document.getElementById('venue-community-section');
        if (communitySection) {
          communitySection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        } else {
          // Fallback: try to scroll to the mobile display buttons area
          const mobileButtons = document.querySelector('[data-testid="mobile-display-buttons"]');
          if (mobileButtons) {
            mobileButtons.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }, 200); // Increased timeout to ensure DOM is ready


      // Clear the parameter to prevent re-triggering
      navigation.setParams({ ...route.params, scrollToDiscussion: false });
    }
  }, [scrollToDiscussion, loading, navigation, route.params]);

  const loadDiscussionMessages = async () => {
    try {
      const db = getFirestore();
      const q = query(
        collection(db, `venues/${venueId}/discussions`),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const messageData = [];

      querySnapshot.forEach((doc) => {
        messageData.push({ id: doc.id, ...doc.data() });
      });

      setMessages(messageData);
    } catch (error) {
      console.error('Error loading discussion messages:', error);
    }
  };

  const gradeData = [
    // { title: "Average\nEmployee Grade", grade: getLetterGrade(venueData?.approved?.employeeGrade) },
    // { title: "Average\nArtist Grade", grade: getLetterGrade(venueData?.approved?.artistGrade) },
    // { title: "Average\nTouring Crew Grade", grade: getLetterGrade(venueData?.approved?.crewGrade) }
  ];

  const categoryRows = [
    ["Shift Length", "Backstage", "Venue Management"],
    ["Break Time", "Hospitality", "Audio Staff"],
    ["Overtime", "Backline/Equipment", "Lighting Staff"],
    ["Scheduling", "Stage Safety", "Cleanliness and Space"],
    ["House Equipment", "Audio Staff", "Safety"],
    ["Cleanliness", "Lighting Staff", "Communication"]
  ];

  const handleSearch = (text) => {
    setSearchQuery(text);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      // Create a structured search object with advanced search parameters
      const searchParams = {
        searchQuery,
        timestamp: Date.now(),
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        nearMe: userLocation ? true : false,
        sourceVenue: venueId,
        searchContext: {
          affiliate: venueData?.affiliate,
          capacity: venueData?.capacity,
          fromVenue: venueData?.name
        }
      };

      navigation.navigate('VenueList', searchParams);
    }
  };

  const handleAffiliatePress = () => {
    if (venueData?.affiliate) {
      const searchParams = {
        affiliate: venueData.affiliate,
      };
      navigation.navigate('VenueList', searchParams);
    }
  };

  const handleAddTechInfo = () => {
    setShowReportForm(true);
    navigation.setParams({ ...route.params, isTechSpecsOnly: true });
  };

  // Manual editing functions for admin
  const handleStartEditingManuals = () => {
    setEditableManuals([...(venueData?.manualsLinks || [])]);
    setIsEditingManuals(true);
  };

  const handleCancelEditingManuals = () => {
    setIsEditingManuals(false);
    setEditableManuals([]);
  };

  const handleAddManual = () => {
    setEditableManuals([...editableManuals, { name: '', url: '' }]);
  };

  const handleDeleteManual = (index) => {
    const newManuals = editableManuals.filter((_, i) => i !== index);
    setEditableManuals(newManuals);
  };

  const handleMoveManual = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editableManuals.length - 1)
    ) {
      return;
    }

    const newManuals = [...editableManuals];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newManuals[index], newManuals[newIndex]] = [newManuals[newIndex], newManuals[index]];
    setEditableManuals(newManuals);
  };

  const handleUpdateManual = (index, field, value) => {
    const newManuals = [...editableManuals];
    newManuals[index] = { ...newManuals[index], [field]: value };
    setEditableManuals(newManuals);
  };

  const handleSaveManuals = async () => {
    if (!auth.currentUser || !isAdmin) {
      alert('Admin access required');
      return;
    }

    setSavingManuals(true);
    
    // Optimistic update - update UI immediately
    const previousVenueData = venueData;
    const previousEditingState = isEditingManuals;
    setVenueData({ ...venueData, manualsLinks: editableManuals });
    setIsEditingManuals(false);
    
    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);

      await updateDoc(venueRef, {
        manualsLinks: editableManuals,
        manualsUpdatedAt: serverTimestamp(),
        manualsUpdatedBy: {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email
        }
      });
    } catch (error) {
      console.error('Error updating manuals:', error);
      // Revert optimistic update on error
      setVenueData(previousVenueData);
      setIsEditingManuals(previousEditingState);
      alert('Error updating manuals: ' + error.message);
    } finally {
      setSavingManuals(false);
    }
  };

  // Load available manuals from Firebase
  const loadAvailableManuals = async () => {
    try {
      setLoadingManuals(true);
      const db = getFirestore();
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));

      const manuals = [];
      manualsSnapshot.forEach((doc) => {
        manuals.push({
          keyword: doc.id,
          ...doc.data()
        });
      });

      setAvailableManuals(manuals);
    } catch (error) {
      console.error('Error loading available manuals:', error);
      setAvailableManuals([]);
    } finally {
      setLoadingManuals(false);
    }
  };

  const handleSelectExistingManual = (manual) => {
    const manualEntry = {
      name: manual.name,
      url: manual.url,
      ...(manual.software?.name && manual.software?.url ? {
        software: {
          name: manual.software.name,
          url: manual.software.url
        }
      } : {}),
      ...(manual.category ? { category: manual.category } : {}),
      ...(manual.manufacturer ? { manufacturer: manual.manufacturer } : {}),
      ...(manual.model ? { model: manual.model } : {}),
      ...(manual.type ? { type: manual.type } : {}),
      keyword: manual.keyword
    };

    setEditableManuals([...editableManuals, manualEntry]);
    setShowManualSelector(false);
    setManualSearchQuery('');
  };

  // Spec Sheets editing functions for admin
  const handleStartEditingSpecSheets = () => {
    setEditableSpecSheets([...(venueData?.specSheets || [])]);
    setIsEditingSpecSheets(true);
  };

  const handleCancelEditingSpecSheets = () => {
    setIsEditingSpecSheets(false);
    setEditableSpecSheets([]);
  };

  const handleAddSpecSheet = () => {
    setEditableSpecSheets([...editableSpecSheets, { name: '', url: '', specType: 'url' }]);
  };

  const handleDeleteSpecSheet = async (index) => {
    const specToDelete = editableSpecSheets[index];

    // If it's a file type, delete it from storage
    if (specToDelete?.specType === 'file' && specToDelete?.url) {
      try {
        // Extract the file path from the download URL
        // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
        const url = new URL(specToDelete.url);
        const pathMatch = url.pathname.match(/\/o\/(.+)/);

        if (pathMatch) {
          const encodedPath = pathMatch[1];
          const filePath = decodeURIComponent(encodedPath);

          const storage = getStorage();
          const fileRef = storageRef(storage, filePath);
          await deleteObject(fileRef);
          console.log('Deleted file from storage:', filePath);
        }
      } catch (error) {
        console.error('Error deleting file from storage:', error);
        // Continue with deletion even if storage deletion fails
      }
    }

    const newSpecSheets = editableSpecSheets.filter((_, i) => i !== index);
    setEditableSpecSheets(newSpecSheets);
  };

  const handleMoveSpecSheet = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editableSpecSheets.length - 1)
    ) {
      return;
    }

    const newSpecSheets = [...editableSpecSheets];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSpecSheets[index], newSpecSheets[newIndex]] = [newSpecSheets[newIndex], newSpecSheets[index]];
    setEditableSpecSheets(newSpecSheets);
  };

  const handleUpdateSpecSheet = (index, field, value) => {
    const newSpecSheets = [...editableSpecSheets];
    newSpecSheets[index] = { ...newSpecSheets[index], [field]: value };
    setEditableSpecSheets(newSpecSheets);
  };

  const handleUploadSpecFile = async (index) => {
    try {
      setUploadingSpecIndex(index);
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*' // Accept any file type
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;

        // Delete old file if it exists and is a file type
        const oldSpec = editableSpecSheets[index];
        if (oldSpec?.specType === 'file' && oldSpec?.url) {
          try {
            const oldUrl = new URL(oldSpec.url);
            const pathMatch = oldUrl.pathname.match(/\/o\/(.+)/);
            if (pathMatch) {
              const encodedPath = pathMatch[1];
              const oldFilePath = decodeURIComponent(encodedPath);
              const storage = getStorage();
              const oldFileRef = storageRef(storage, oldFilePath);
              await deleteObject(oldFileRef);
              console.log('Deleted old file from storage:', oldFilePath);
            }
          } catch (deleteError) {
            console.error('Error deleting old file from storage:', deleteError);
            // Continue with upload even if old file deletion fails
          }
        }

        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const storage = getStorage();
        const fileName = `specSheetMedia/${venueId}/${Date.now()}_${file.name}`;
        const storageRefPath = storageRef(storage, fileName);

        await uploadBytes(storageRefPath, fileBlob);
        const downloadURL = await getDownloadURL(storageRefPath);

        const newSpecSheets = [...editableSpecSheets];
        newSpecSheets[index] = {
          ...newSpecSheets[index],
          url: downloadURL,
          specType: 'file',
          fileName: file.name,
          fileSize: file.size
        };
        setEditableSpecSheets(newSpecSheets);

        alert('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading spec sheet file:', error);
      alert('Error uploading file: ' + error.message);
    } finally {
      setUploadingSpecIndex(null);
    }
  };

  const handleSaveSpecSheets = async () => {
    if (!auth.currentUser || !isAdmin) {
      alert('Admin access required');
      return;
    }

    setSavingSpecSheets(true);
    
    // Optimistic update - update UI immediately
    const previousVenueData = venueData;
    const previousEditingState = isEditingSpecSheets;
    setVenueData({ ...venueData, specSheets: editableSpecSheets });
    setIsEditingSpecSheets(false);
    
    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);

      await updateDoc(venueRef, {
        specSheets: editableSpecSheets,
        specSheetsUpdatedAt: serverTimestamp(),
        specSheetsUpdatedBy: {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email
        }
      });
    } catch (error) {
      console.error('Error updating spec sheets:', error);
      // Revert optimistic update on error
      setVenueData(previousVenueData);
      setIsEditingSpecSheets(previousEditingState);
      alert('Error updating spec sheets: ' + error.message);
    } finally {
      setSavingSpecSheets(false);
    }
  };


  const handleSaveVenue = async () => {
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }



    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const savedVenues = userDoc.data().savedVenues || [];

        if (isSaved) {
          // Remove venue from saved list
          const updatedVenues = savedVenues.filter(id => id !== venueId);
          await updateDoc(userRef, {
            savedVenues: updatedVenues
          });
          setIsSaved(false);
          alert('Venue removed from saved venues');
        } else {
          // Add venue to saved list
          if (!savedVenues.includes(venueId)) {
            await updateDoc(userRef, {
              savedVenues: arrayUnion(venueId)
            });
            setIsSaved(true);
            alert('Venue saved successfully!');
          }
        }
      } else {
        // If user doc doesn't exist, create it with this venue
        await updateDoc(userRef, {
          savedVenues: [venueId]
        });
        setIsSaved(true);
        alert('Venue saved successfully!');
      }
    } catch (error) {
      console.error('Error updating venue save status:', error);
      alert('Failed to update venue save status. Please try again.');
    }
  };

  const handleFormSubmit = () => {
    setShowReportForm(false);
    navigation.setParams({
      ...route.params,
      isTechSpecsOnly: undefined
    });
    navigation.navigate('MyReports');
  };

  const handleFormCancel = () => {
    setShowReportForm(false);
    // Also clear the isTechSpecsOnly parameter from route params
    navigation.setParams({
      ...route.params,
      isTechSpecsOnly: undefined // or delete this parameter
    });
  };

  const handleSubmitReport = () => {
    setShowReportForm(true);
  };

  const handleAddToTour = () => {
    if (canAddToTour()) {
      // Navigate to tour planner with the current venue
      navigation.navigate('TourPlanner', {
        addVenue: {
          id: venueId,
          name: venueData?.name,
          city: venueData?.city,
          state: venueData?.state,
          address: venueData?.fullAddress,
          latitude: venueData?.latitude,
          longitude: venueData?.longitude,
          backgroundImage: venueData?.backgroundImage
        }
      });
    } else {
      // Show coming soon message for non-admin users
      Alert.alert(
        "Tour Planner Coming Soon!",
        "We're working on making the Tour Planner available to all users. Stay tuned for updates!",
        [{ text: "OK" }]
      );
    }
  };

  const canAddToTour = () => {
    return userType === 'admin';
  };

  const getSubmitButtonStyle = () => {
    return [
      venueStyles.actionButton,
      { backgroundColor: '#666769' }
    ];
  };

  const getAddToTourButtonStyle = () => {
    return [
      venueStyles.actionButton,
      { backgroundColor: '#666769' }
    ];
  };

  const getSaveButtonStyle = () => {
    return {
      ...venueStyles.saveButton,
      transform: isSaveHovered ? 'rotate(10deg) scale(1.2)' : 'rotate(10deg)',
      filter: isSaveHovered ? 'brightness(1.2)' : 'brightness(1)',
      transition: 'transform 0.3s ease-in-out, filter 0.3s ease-in-out'
    };
  };

  const handleVote = async (voteTarget, voteType) => {
    // if (!auth.currentUser) {
    //   navigation.navigate('Login');
    //   return;
    // }

    // Define vote configurations based on target
    const voteConfig = {
      'venue': {
        stateGetter: votes,
        stateSetter: setVotes,
        userVoteGetter: userVote,
        userVoteSetter: setUserVote,
        votePrefix: '',
        up: 'upVotes',
        down: 'downVotes',
        storageKey: `venue_${venueId}_vote`
      },
      'title': {
        stateGetter: titleVotes,
        stateSetter: setTitleVotes,
        userVoteGetter: userTitleVote,
        userVoteSetter: setUserTitleVote,
        votePrefix: 'title',
        up: 'titleUpVotes',
        down: 'titleDownVotes',
        storageKey: `venue_${venueId}_title_vote`
      },
      'specs': {
        stateGetter: specsVotes,
        stateSetter: setSpecsVotes,
        userVoteGetter: userSpecsVote,
        userVoteSetter: setUserSpecsVote,
        votePrefix: 'specs',
        up: 'specsUpVotes',
        down: 'specsDownVotes',
        storageKey: `venue_${venueId}_specs_vote`
      },
      'links': {
        stateGetter: linksVotes,
        stateSetter: setLinksVotes,
        userVoteGetter: userLinksVote,
        userVoteSetter: setUserLinksVote,
        votePrefix: 'links',
        up: 'linksUpVotes',
        down: 'linksDownVotes',
        storageKey: `venue_${venueId}_links_vote`
      }
    };

    const config = voteConfig[voteTarget];
    if (!config) {
      console.error('Invalid vote target:', voteTarget);
      return;
    }

    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);
      // const userVoteRef = doc(
      //   db, 
      //   'venues', 
      //   venueId, 
      //   voteTarget == 'venue' ? 'votes' : `${voteTarget}Votes`, 
      //   auth.currentUser.uid
      // );


      // Get existing vote from local storage
      const existingVote = localStorage.getItem(config.storageKey);

      // If user already voted the same way, remove their vote
      if (existingVote === voteType) {
        await updateDoc(venueRef, {
          [config[voteType]]: increment(-1)
        });
        // await deleteDoc(userVoteRef);
        localStorage.removeItem(config.storageKey);
        config.userVoteSetter(null);
        config.stateSetter(prev => ({
          ...prev,
          [voteType]: prev[voteType] - 1
        }));
        return;
      }

      // If user is changing their vote
      if (existingVote) {
        await updateDoc(venueRef, {
          // [config[config.userVoteGetter]]: increment(-1),
          [config[existingVote]]: increment(-1),
          [config[voteType]]: increment(1)
        });
      } else {
        // First time voting
        await updateDoc(venueRef, {
          [config[voteType]]: increment(1)
        });
      }

      // Save user's vote
      // await setDoc(userVoteRef, {
      //   vote: voteType,
      //   timestamp: serverTimestamp()
      // });
      // Save vote to local storage
      localStorage.setItem(config.storageKey, voteType);

      config.userVoteSetter(voteType);
      config.stateSetter(prev => ({
        ...prev,
        [voteType]: prev[voteType] + 1,
        // [config.userVoteGetter]: config.userVoteGetter ? prev[config.userVoteGetter] - 1 : prev[config.userVoteGetter]
        [existingVote]: existingVote ? prev[existingVote] - 1 : prev[existingVote]
      }));

    } catch (error) {
      console.error('Error updating vote:', error);
      alert('Failed to update vote. Please try again.');
    }
  };

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  const handlePrevPhoto = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);
    const newIndex = selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : discussionPhotos.length - 1;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(discussionPhotos[newIndex]);
  };

  const handleNextPhoto = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);
    const newIndex = selectedPhotoIndex < discussionPhotos.length - 1 ? selectedPhotoIndex + 1 : 0;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(discussionPhotos[newIndex]);
  };

  const renderPhotoModal = () => {
    if (!selectedPhoto) return null;

    return (
      <View style={venueStyles.photoModal}>
        <View style={venueStyles.modalContent}>
          <TouchableOpacity style={venueStyles.modalClose} onPress={handleCloseModal}>
            <Text style={{ color: 'white', fontSize: 24 }}>×</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[venueStyles.modalNav, venueStyles.modalNavPrev]} onPress={handlePrevPhoto}>
            <Text style={{ color: 'white', fontSize: 48 }}>‹</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[venueStyles.modalNav, venueStyles.modalNavNext]} onPress={handleNextPhoto}>
            <Text style={{ color: 'white', fontSize: 48 }}>›</Text>
          </TouchableOpacity>

          <View style={{ width: '100%', height: '100%', minHeight: 320, justifyContent: 'center', alignItems: 'center' }}>
            <Image
              source={{ uri: selectedPhoto.photoURL }}
              style={{ width: '90%', height: '90%', objectFit: 'contain' }}
              resizeMode="contain"
            />
          </View>

          <View style={venueStyles.modalInfo}>
            <Text style={venueStyles.modalUsername}>{selectedPhoto.username}</Text>
            {selectedPhoto.timestamp && (
              <Text style={venueStyles.modalTimestamp}>
                {new Date(selectedPhoto.timestamp.seconds * 1000).toLocaleDateString()}
              </Text>
            )}
          </View>
        </View>
      </View>
    );
  };

  const renderReports = () => {
    if (!discussionData?.reports || discussionData.reports.length === 0) {
      return (
        <View style={venueStyles.emptyStateContainer}>
          <Text style={venueStyles.emptyStateText}>No reports available for this venue</Text>
        </View>
      );
    }

    return (
      <ScrollView style={venueStyles.reportsContainer}>
        {discussionData.reports.map((report, index) => (
          <View key={index} style={venueStyles.reportCard}>
            <View style={venueStyles.reportHeader}>
              <View style={venueStyles.reportUserInfo}>
                <Text style={venueStyles.reportUserName}>{report.userName || 'Anonymous'}</Text>
                <Text style={venueStyles.reportDate}>
                  {report.createdAt ? new Date(report.createdAt.seconds * 1000).toLocaleDateString() : 'Date not available'}
                </Text>
              </View>
              <View style={venueStyles.reportGrade}>
                <Text style={venueStyles.gradeLetter}>{getLetterGrade(report.averageGrade)}</Text>
              </View>
            </View>

            <View style={venueStyles.reportContent}>
              <Text style={venueStyles.reportExperience}>{report.overallExperience}</Text>

              <View style={venueStyles.ratingsGrid}>
                {report.safetyRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Safety</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.safetyRating)}</Text>
                  </View>
                )}
                {report.equipmentRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Equipment</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.equipmentRating)}</Text>
                  </View>
                )}
                {report.staffRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Staff</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.staffRating)}</Text>
                  </View>
                )}
                {report.workingConditionsRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Working Conditions</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.workingConditionsRating)}</Text>
                  </View>
                )}
                {report.breakTimeRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Break Time</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.breakTimeRating)}</Text>
                  </View>
                )}
                {report.overtimeRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Overtime</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.overtimeRating)}</Text>
                  </View>
                )}
                {report.schedulingRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Scheduling</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.schedulingRating)}</Text>
                  </View>
                )}
              </View>

              {report.photos && report.photos.length > 0 && (
                <View style={venueStyles.mediaSection}>
                  <Text style={venueStyles.mediaLabel}>Photos:</Text>
                  <ScrollView horizontal style={venueStyles.mediaScroll}>
                    {report.photos.map((photo, photoIndex) => (
                      <Image
                        key={photoIndex}
                        source={{ uri: photo }}
                        style={venueStyles.mediaPreview}
                      />
                    ))}
                  </ScrollView>
                </View>
              )}

              {report.notes && (
                <View style={venueStyles.notesSection}>
                  <Text style={venueStyles.notesLabel}>Additional Notes:</Text>
                  <Text style={venueStyles.notesText}>{report.notes}</Text>
                </View>
              )}
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const toggleMobileDisplay = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  }

  const renderMobileDisplayButtons = () => {
    const MobileButton = ({ label, onPress, isActive = false, size = "1rem", textStyle = {} }) => (
      <TouchableOpacity style={venueStyles.mobileButton} onPress={onPress}>
        <Text
          style={[
            venueStyles.mobileButtonText,
            isActive ? venueStyles.activeMobileButtonText : venueStyles.inactiveMobileButtonText,
            { fontSize: size, ...textStyle }
          ]}
        >
          {label}
        </Text>
      </TouchableOpacity>
    )


    return (
      <View data-testid="mobile-display-buttons" style={venueStyles.mobileDisplayButtons}>
        <View style={venueStyles.mobileDisplayButtonsRow}>
          <MobileButton label="VIEW SPECS" onPress={() => toggleMobileDisplay('report')} size={isMobile || isTablet ? '1rem' : '1.2rem'} isActive={activeSection === 'report'} />
          <MobileButton label={isSaved ? "VENUE SAVED" : "SAVE VENUE"} onPress={handleSaveVenue} isActive={isSaved} size={isMobile || isTablet ? '1rem' : '1.2rem'} />
          <MobileButton label="LINKS" onPress={() => toggleMobileDisplay('links')} size={isMobile || isTablet ? '1rem' : '1.2rem'} isActive={activeSection === 'links'} />
          <MobileButton label="SHARE VENUE SPECS" onPress={handleAddTechInfo} size={isMobile || isTablet ? '1rem' : '1.25rem'} />
          <MobileButton label="REPORT CARDS" onPress={handleSubmitReport} size={isMobile || isTablet ? '1rem' : '1.2rem'} />
          <MobileButton textStyle={{ letterSpacing: 0 }} label="COMMUNITY" onPress={() => toggleMobileDisplay('discussionsAndUploads')} isActive={activeSection === 'discussionsAndUploads'} size={isMobile || isTablet ? '0.9rem' : '1.2rem'} />
        </View>

      </View>
    )
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'report':
        return (
          <View style={venueStyles.leftColumn} data-column="left">
            <View>
              <View style={[venueStyles.gradeCardGroup, { width: '100%' }]}>
                {gradeData.map((data, index) => (
                  <TouchableOpacity
                    key={index}
                    style={{
                      ...venueStyles.gradeCard,
                      width: '100%',
                      maxWidth: '100%',
                      alignSelf: 'stretch',
                      flex: 1
                    }}
                    onPress={() => setShowCategories(data.title)}
                  >
                    <Text style={venueStyles.gradeCardTitle}>{data.title}</Text>
                    <Text style={venueStyles.gradeCardGrade}>{data.grade}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {showCategories ? (
                <View>
                  <TouchableOpacity
                    style={venueStyles.backButton}
                    onPress={() => setShowCategories(false)}
                  >
                    <Text style={venueStyles.backButtonText}>← Back to Specs</Text>
                  </TouchableOpacity>
                  <View style={venueStyles.categoryContainer}>
                    {categoryRows.map((row, rowIndex) => (
                      <View key={rowIndex} style={venueStyles.categoryRow}>
                        {row.map((category, index) => (
                          <CategoryButton
                            key={index}
                            text={category}
                            onPress={() => {
                              // Handle category press
                              alert(`${category} reports coming soon...`);
                            }}
                          />
                        ))}
                      </View>
                    ))}
                  </View>
                </View>
              ) : (
                <>
                  <View style={[
                    venueStyles.techSpecsContainer,
                    getResponsiveValue({
                      mobile: venueStyles.techSpecMobileView,
                      tablet: {},
                      laptop: {},
                      desktop: {}
                    }),
                    {
                      marginTop: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      minHeight: 'auto',
                      height: 'auto',
                      overflow: 'visible',
                      flexGrow: 1,
                      flexShrink: 0
                    }
                  ]}>
                    {[
                      { label: 'Capacity', value: venueData?.capacity },
                      { label: 'ADA Accessibility for Artist', value: venueData?.adaAccessibilityArtist },
                      { label: 'Parking', value: venueData?.parking },
                      { label: 'Shore Power', value: venueData?.shorePower },
                      { label: 'Backstage', value: venueData?.backstage },
                      { label: 'Merch', value: venueData?.merch },
                      { label: 'Stage', value: venueData?.stage },
                      { label: 'Backline', value: venueData?.backline },
                      { label: 'Video', value: venueData?.video },
                      { label: 'Monitors', value: venueData?.monitors },
                      { label: 'PA System', value: venueData?.pa },
                      { label: 'FOH', value: venueData?.foh },
                      { label: 'Lighting Control', value: venueData?.lightingControl },
                      { label: 'Lighting Fixtures', value: venueData?.lightingFixtures },
                      { label: 'Notes', value: venueData?.notes }
                    ].map((spec, index, array) => (
                      <View key={spec.label} style={{
                        width: '100%',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}>
                        <View style={{
                          width: '80%',
                          paddingVertical: 15,
                          alignItems: 'flex-start',
                          flexShrink: 0,
                        }}>
                          <Text style={{
                            fontSize: getResponsiveValue({
                              mobile: 18,
                              tablet: 20,
                              laptop: 20,
                              desktop: 20
                            }),
                            fontWeight: 'bold',
                            color: '#000000',
                            marginBottom: 8,
                            fontFamily: 'VastagoGrotesk',
                            width: '100%',
                            textAlign: 'center',
                            flexWrap: 'wrap',
                          }}>{spec.label}:</Text>
                          {/* <Text style={{ 
                            fontSize: getResponsiveValue({
                              mobile: 16,
                              tablet: 18,
                              laptop: 18,
                              desktop: 18
                            }),
                            color: '#000000',
                            opacity: spec.value ? 1 : 0.4,
                            fontFamily: 'VastagoGrotesk',
                            flexWrap: 'wrap',
                            ...(Platform.OS === 'web' && spec.label !== 'Notes' ? {
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none',
                              userSelect: 'none'
                            } : {})
                          }}>{spec.value || 'Unknown'}</Text> */}
                          {spec.value ? (
                            String(spec.value).split(/\r?\n/).map((line, lineIndex) => {
                              // URL detection regex
                              const urlRegex = /(https?:\/\/[^\s]+)/g;
                              const parts = line.split(urlRegex);

                              return (
                                <Text
                                  key={lineIndex}
                                  style={{
                                    fontSize: getResponsiveValue({
                                      mobile: 16,
                                      tablet: 18,
                                      laptop: 18,
                                      desktop: 18
                                    }),
                                    color: '#000000',
                                    opacity: 1,
                                    fontFamily: 'VastagoGrotesk',
                                    flexWrap: 'wrap',
                                    width: '100%',
                                    textAlign: line.includes('•') || line.includes('▪') ? 'left' : (lineIndex === 0 ? 'center' : 'left'),
                                    minHeight: '1em',  // Ensure empty lines take up space
                                  }}
                                >
                                  {parts.map((part, partIndex) => {
                                    if (part.match(urlRegex)) {
                                      return (
                                        <Text
                                          key={partIndex}
                                          style={{
                                            color: '#00A575',
                                            textDecorationLine: 'underline',
                                            ...(Platform.OS === 'web' ? {
                                              cursor: 'pointer',
                                              WebkitUserSelect: 'text',
                                              MozUserSelect: 'text',
                                              msUserSelect: 'text',
                                              userSelect: 'text'
                                            } : {})
                                          }}
                                          onPress={() => {
                                            if (Platform.OS === 'web') {
                                              window.open(part, '_blank');
                                            } else {
                                              Linking.openURL(part);
                                            }
                                          }}
                                        >
                                          {part}
                                        </Text>
                                      );
                                    }
                                    return part || ' ';
                                  })}
                                </Text>
                              );
                            })
                          ) : (
                            <Text style={{
                              fontSize: getResponsiveValue({
                                mobile: 16,
                                tablet: 18,
                                laptop: 18,
                                desktop: 18
                              }),
                              color: '#000000',
                              opacity: 0.4,
                              fontFamily: 'VastagoGrotesk',
                              flexWrap: 'wrap',
                              width: '100%',
                              textAlign: 'center',
                            }}>
                              Unknown
                            </Text>)
                          }
                        </View>
                        {index < array.length - 1 && (
                          <View style={{
                            width: '60%',
                            height: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            marginVertical: 5
                          }} />
                        )}
                      </View>
                    ))}
                  </View>

                  {/* Specs voting section */}
                  <View style={[venueStyles.smallerVotingContainer, {
                    marginTop: 20,
                    marginBottom: 40,
                  }]}>
                    <Text style={venueStyles.smallerVotingText}>Is this correct?</Text>

                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <TouchableOpacity
                        onPress={() => handleVote('specs', 'up')}
                        style={[venueStyles.voteButton, {
                          marginRight: 10,
                          opacity: userSpecsVote === 'up' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons
                          name={userSpecsVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
                          size={16}
                          color="#000000"
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{specsVotes.up}</Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={() => handleVote('specs', 'down')}
                        style={[venueStyles.voteButton, {
                          opacity: userSpecsVote === 'down' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons
                          name={userSpecsVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
                          size={16}
                          color="#000000"
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{specsVotes.down}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </>
              )}
            </View>
          </View>
        );

      case 'discussionsAndUploads':
        return (
          <View id="venue-community-section" style={venueStyles.discussionsAndUploadsContainer}>
            <View style={venueStyles.discussionAndUploadsBox}>
              {activeDiscussionTab === 'discussions' ? (
                <DiscussionBoard
                  venueId={venueId}
                  discussions={discussionData?.discussions || []}
                  reports={discussionData?.reports || []}
                  textStyle={venueStyles.discussionText}
                  inputStyle={venueStyles.discussionInput}
                  placeholderStyle={venueStyles.discussionPlaceholder}
                  containerStyle={venueStyles.discussionSection}
                />
              ) : renderMedia()}
            </View>
            <View style={venueStyles.discussionsAndUploadsTabs}>
              <TouchableOpacity
                style={venueStyles.discussionsAndUploadsTab}
                onPress={() => setActiveDiscussionTab('discussions')}
              >
                <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'discussions' ? 20 : 14 }]}>NOTES</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={venueStyles.discussionsAndUploadsTab}
                onPress={() => setActiveDiscussionTab('uploads')}
              >
                <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'uploads' ? 20 : 14 }]}>UPLOADS</Text>
              </TouchableOpacity>
            </View>
          </View>
        );

      case 'tour':
        return (
          <View style={venueStyles.tourSection}>
            <Text style={venueStyles.comingSoonText}>Tour planning coming soon...</Text>
          </View>
        );

      case 'links':
        return (
          <View style={venueStyles.mobileManualsContainer}>
            {renderManualsButtons()}
          </View>
        )


      default:
        return null;

    }
  };

  const renderSpecSheets = () => {

    if (!venueData?.specSheets && venueData?.specSheetLink) {
      return (
        <TouchableOpacity
          style={[venueStyles.manualButton, { marginBottom: isMobile ? 40 : 20, backgroundColor: '#00A575' }]}
          onPress={() => window.open(venueData.specSheetLink, '_blank')}
        >
          <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>{'Venue\'s Spec Link'}</Text>
        </TouchableOpacity>
      )
    }

    // If admin is editing spec sheets, show the editor
    if (isAdmin && isEditingSpecSheets) {
      return renderSpecSheetsEditor();
    }

    if (venueData?.specSheets && venueData?.specSheets.length > 0) {
      return (
        <View style={{ width: '100%' }}>
          {isAdmin && (
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end'}}>
              <TouchableOpacity
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 8,
                  borderRadius: 5,
                  alignItems: 'center',
                }}
                onPress={handleStartEditingSpecSheets}
              >
                <Text style={{ color: '#3D7FE6', fontSize: 12, fontWeight: 'bold' }}>EDIT SPEC SHEETS</Text>
              </TouchableOpacity>
            </View>
          )}
          <View style={venueStyles.specSheetsContainer}>
            {venueData?.specSheets.map((spec, index) => (
              <TouchableOpacity
                key={index}
                style={[venueStyles.horizontalManualButton, { backgroundColor: spec.specType === 'url' ? '#00A575' : '#000000', width: 'auto' }]}
                onPress={() => {
                  if (spec.url) {
                    window.open(spec.url, '_blank');
                  }
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>{spec.name || 'Spec Sheet'}</Text>
                  {spec.specType === 'file' && (
                    <Text style={{ color: '#ffffff', fontSize: 12, marginLeft: 10 }}>
                      📄
                    </Text>
                  )}
                  {spec.specType === 'url' && (
                    <Text style={{ color: '#ffffff', fontSize: 12, marginLeft: 10 }}>🔗</Text>
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )
    }
    else {
      return (
        <View style={{ width: '100%', marginBottom: 40 }}>
          <Text style={venueStyles.noContentText}>No spec sheets available</Text>
          {isAdmin && (
            <TouchableOpacity
              style={[venueStyles.manualButton, { backgroundColor: '#2980b9', marginTop: 10 }]}
              onPress={handleStartEditingSpecSheets}
            >
              <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>Add Spec Sheets (Admin)</Text>
            </TouchableOpacity>
          )}
        </View>
      )
    }
  }

  const renderManualsButtons = () => {
    return (
      <View style={{
        flex: 1,
        padding: isMobile ? 0 : 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: isMobile ? 5 : 20
      }}>
        {/* Show venue's spec sheet if available */}

        <View style={{ width: '100%' }}>
          <Text style={venueStyles.manualHeaderText}>Spec Sheets</Text>
        </View>
        {renderSpecSheets()}
        {/* {venueData?.specSheetLink && (
          <TouchableOpacity
            style={[venueStyles.manualButton, { marginBottom: isMobile ? 40 : 20, backgroundColor: '#00A575' }]}
            onPress={() => window.open(venueData.specSheetLink, '_blank')}
          >
            <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>{'Venue\'s Spec Link'}</Text>
          </TouchableOpacity>
        )} */}

        {/* Show any tech specs documents */}
        {venueData?.techSpecs?.map((spec, index) => (
          <TouchableOpacity
            key={index}
            style={[venueStyles.manualButton, { marginBottom: 20 }]}
            onPress={() => window.open(spec.url, '_blank')}
          >
            <Text style={venueStyles.manualButtonText}>{spec.name || 'Tech Specs'}</Text>
          </TouchableOpacity>
        ))}

        {/* Show relevant equipment manuals */}
        <View style={{ width: '100%' }}>
          <View style={{ width: '100%' }}>
            <Text style={venueStyles.manualHeaderText}>Manual Links</Text>
          </View>
          {renderManualSection(venueData?.manualsLinks || findRelevantManuals(venueData))}

          {/* Links voting section */}
          <View style={[venueStyles.smallerVotingContainer, {
            marginTop: 20,
            marginBottom: 10,
          }]}>
            <Text style={venueStyles.smallerVotingText}>Is this correct?</Text>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <TouchableOpacity
                onPress={() => handleVote('links', 'up')}
                style={[venueStyles.voteButton, {
                  marginRight: 10,
                  opacity: userLinksVote === 'up' ? 1 : 0.7,
                  flexDirection: 'row',
                  alignItems: 'center'
                }]}
              >
                <Ionicons
                  name={userLinksVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
                  size={16}
                  color="#000000"
                />
                <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{linksVotes.up}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => handleVote('links', 'down')}
                style={[venueStyles.voteButton, {
                  opacity: userLinksVote === 'down' ? 1 : 0.7,
                  flexDirection: 'row',
                  alignItems: 'center'
                }]}
              >
                <Ionicons
                  name={userLinksVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
                  size={16}
                  color="#000000"
                />
                <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{linksVotes.down}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    )
  }

  const renderMedia = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);

    if (discussionPhotos.length === 0) {
      return (
        <View style={venueStyles.emptyStateContainer}>
          <Text style={venueStyles.emptyStateText}>No user photos available</Text>
        </View>
      );
    }

    return (
      <>
        <ScrollView style={venueStyles.photoCollageContainer}>
          <View style={venueStyles.photoCollageGrid}>
            {discussionPhotos.map((msg, index) => (
              <TouchableOpacity
                key={msg.id}
                style={venueStyles.mobilePhotoCollageItem}
                onPress={() => handlePhotoClick(msg, index)}
              >
                <Image
                  source={{ uri: msg.photoURL }}
                  style={venueStyles.mobilePhotoCollageImage}
                />
                <Text style={[venueStyles.mobilePhotoCollageTimestamp, { width: '100%', textAlign: 'left' }]}>{new Date(msg.timestamp.toDate()).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} </Text>
                <View style={venueStyles.mobilePhotoCollageUsernameContainer}>
                  <Text style={venueStyles.mobilePhotoCollageTimestamp}>{new Date(msg.timestamp.toDate()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} </Text>
                  <Text style={venueStyles.mobilePhotoCollageUsername}>{msg.username} </Text>
                </View>
              </TouchableOpacity>
              //   )
              // : (
              //   <TouchableOpacity
              //     key={msg.id}
              //     style={venueStyles.photoCollageItem}
              //     onPress={() => handlePhotoClick(msg, index)}
              //   >
              //     <Image
              //       source={{ uri: msg.photoURL }}
              //       style={venueStyles.photoCollageImage}
              //     />
              //     <Text style={venueStyles.photoCollageUsername}>{msg.username}</Text>
              //   </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
        {renderPhotoModal()}
      </>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'map':
        return (
          <View style={venueStyles.mediaContainer}>
            {venueData?.mapEmbedLink ? (
              <div
                style={venueStyles.mapContainer}
                dangerouslySetInnerHTML={{
                  __html: venueData.mapEmbedLink
                    // Ensure iframe tags are properly rendered
                    .replace(/&lt;iframe/g, '<iframe')
                    .replace(/&lt;\/iframe&gt;/g, '</iframe>')
                    .replace(/&amp;/g, '&')
                    // Make iframe fit container with !important to override any inline styles
                    .replace(/<iframe(.*?)>/, '<iframe$1 style="width:100% !important; height:100% !important; border:0 !important; position:absolute !important; top:0 !important; left:0 !important; right:0 !important; bottom:0 !important;">')
                }}
              />
            ) : (
              <Text style={venueStyles.noContentText}>No map available</Text>
            )}
          </View>
        );

      case 'media':
        return renderMedia();

      case 'manuals':
        return (
          <View style={venueStyles.mediaContainer}>
            {renderManualsButtons()}
          </View>
        );

      default:
        return null;
    }
  };

  const renderOverallGrade = () => {
    const approved = venueData?.approved || {};
    console.log('Rendering overall grade with data:', approved);

    return (
      <View style={[venueStyles.overallGradeCard, { backgroundColor: '#00A575' }]}>
        <TouchableOpacity
          style={getSaveButtonStyle()}
          onPress={handleSaveVenue}
          onMouseEnter={() => setIsSaveHovered(true)}
          onMouseLeave={() => setIsSaveHovered(false)}
        >
          <Image
            source={isSaved ? require('../../assets/imgs/saved_alt.png') : require('../../assets/imgs/save_alt.png')}
            style={venueStyles.saveButtonIcon}
          />
        </TouchableOpacity>
        <Text style={[venueStyles.gradeLetter, { color: 'white' }]}>
          {getLetterGrade(approved.overallGrade)}
        </Text>
        <Text style={[venueStyles.gradeTitle, { color: 'white' }]}>Overall Grade</Text>
        <Text style={[venueStyles.reportCount, { color: 'white' }]}>
          {approved.reportCount || 0} reports
        </Text>
      </View>
    );
  };

  const renderMapCard = () => {
    console.log(venueData?.mapEmbedLink)
    return (
      <View style={venueStyles.mapCard}>
        {venueData?.mapEmbedLink ? (
          <div
            style={{
              width: '100%',
              height: '100%', minHeight: 320,
              borderRadius: 10,
              overflow: 'hidden', position: 'relative'
            }}
            dangerouslySetInnerHTML={{
              __html: venueData.mapEmbedLink ?
                venueData.mapEmbedLink
                  .replace('zoom=14', 'zoom=12')
                  // Ensure iframe tags are properly rendered
                  .replace(/&lt;iframe/g, '<iframe')
                  .replace(/&lt;\/iframe&gt;/g, '</iframe>')
                  .replace(/&amp;/g, '&')
                  .replace(/width="\d+"/, 'width="100%"')
                  .replace(/height="\d+"/, 'height="100%"')
                  .replace(/style="[^"]*"/, 'style="width:100% !important;height:100% !important;min-height:320px !important;border:0 !important;position:absolute !important;top:0 !important;left:0 !important;border-radius:10px !important;"')
                : ''
            }}
          />
        ) : (
          <Text style={venueStyles.noContentText}>No map available</Text>
        )}
      </View>
    );
  };

  const getVenueTextStyle = (label) => {
    return getStyle('venueScreenStyles', label, isMobile ? 'mobile' : 'web');
  }


  // Add this useEffect to handle height synchronization
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Create a ResizeObserver to watch the left column height
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          // Set the height as a CSS variable
          document.documentElement.style.setProperty(
            '--left-column-height',
            `${entry.contentRect.height}px`
          );
        }
      });

      // Get the left column element
      const leftColumn = document.querySelector('[data-column="left"]');
      if (leftColumn) {
        observer.observe(leftColumn);
      }

      return () => observer.disconnect();
    }
  }, []);

  if (loading) {
    return (
      <View style={globalStyles.container}>
        <Text style={globalStyles.loadingText}>Loading...</Text>
      </View>
    );
  }

  // If showing report form, render the form
  // if (showReportForm && venueData) {
  //   return (
  //     <View style={globalStyles.container}>
  //       <BackgroundVideo />
  //       <View style={globalStyles.overlay}>
  //         <VenueReportForm
  //           venue={{
  //             ...venueData,
  //             id: venueId // Explicitly include the ID from route params
  //           }}
  //           onSubmit={handleFormSubmit}
  //           onCancel={handleFormCancel}
  //           isTechSpecsOnly={route.params?.isTechSpecsOnly}
  //         />
  //       </View>
  //     </View>
  //   );
  // }

  const renderReportFormModal = () => {
    return (
      <CustomModal onClose={handleFormCancel}>
        <VenueReportForm
          venue={{
            ...venueData,
            id: venueId // Explicitly include the ID from route params
          }}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isTechSpecsOnly={route.params?.isTechSpecsOnly}
        />
      </CustomModal>
    );
  }


  return (
    <View style={globalStyles.container}>
      <Modal
        visible={showManualSelector}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowManualSelector(false);
          setManualSearchQuery('');
        }}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
          <View style={{
            backgroundColor: '#2c3e50',
            borderRadius: 10,
            padding: 20,
            width: '100%',
            maxWidth: 700,
            maxHeight: '85%',
            ...(Platform.OS === 'web' && {
              border: '2px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
            })
          }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                Select Equipment Manual
              </Text>
              <TouchableOpacity
                style={{
                  padding: 8,
                  borderRadius: 15,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onPress={() => {
                  setShowManualSelector(false);
                  setManualSearchQuery('');
                }}
              >
                <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>✕</Text>
              </TouchableOpacity>
            </View>

            <TextInput
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 8,
                padding: 12,
                color: '#fff',
                marginBottom: 15,
                fontSize: 14,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              placeholder="Search manuals by name or keyword..."
              placeholderTextColor="rgba(255, 255, 255, 0.5)"
              value={manualSearchQuery}
              onChangeText={setManualSearchQuery}
            />

            <Text style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 12,
              marginBottom: 10
            }}>
              {loadingManuals ? 'Loading...' : `${availableManuals.filter(manual => {
                const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                return nameMatch || keywordMatch;
              }).length} of ${availableManuals.length} manuals`}
            </Text>

            <ScrollView style={{ maxHeight: 350, marginBottom: 15 }}>
              {loadingManuals ? (
                <View style={{ textAlign: 'center', padding: 30 }}>
                  <Text style={{ color: '#fff', fontSize: 16 }}>
                    Loading manuals...
                  </Text>
                </View>
              ) : availableManuals.filter(manual => {
                const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                return nameMatch || keywordMatch;
              }).length === 0 ? (
                <View style={{ textAlign: 'center', padding: 30 }}>
                  <Text style={{ color: '#fff', fontSize: 16 }}>
                    {manualSearchQuery ? 'No manuals found matching your search.' : 'No manuals available.'}
                  </Text>
                  {manualSearchQuery && (
                    <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, marginTop: 5 }}>
                      Try searching for different keywords or equipment names.
                    </Text>
                  )}
                </View>
              ) : (
                availableManuals.filter(manual => {
                  const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                  const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                  return nameMatch || keywordMatch;
                }).map((manual) => (
                  <TouchableOpacity
                    key={manual.keyword}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 8,
                      padding: 15,
                      marginBottom: 8,
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                    onPress={() => handleSelectExistingManual(manual)}
                  >
                    <Text style={{
                      color: '#fff',
                      fontWeight: 'bold',
                      marginBottom: 5,
                      fontSize: 14
                    }}>
                      {manual.name}
                    </Text>
                    <Text style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: 11,
                      marginBottom: 3
                    }}>
                      Keyword: {manual.keyword} • URL: {manual.url.length > 50 ? manual.url.substring(0, 50) + '...' : manual.url}
                    </Text>
                    {manual.software?.name && (
                      <Text style={{
                        color: 'rgba(52, 152, 219, 0.9)',
                        fontSize: 11,
                        marginBottom: 3
                      }}>
                        📦 Software: {manual.software.name}
                      </Text>
                    )}
                    {manual.category && (
                      <Text style={{
                        color: 'rgba(46, 204, 113, 0.9)',
                        fontSize: 10,
                        textTransform: 'uppercase'
                      }}>
                        {manual.category}
                      </Text>
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>

            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
              <TouchableOpacity
                style={{
                  backgroundColor: '#95a5a6',
                  padding: 12,
                  borderRadius: 8,
                  minWidth: 100,
                  alignItems: 'center'
                }}
                onPress={() => {
                  setShowManualSelector(false);
                  setManualSearchQuery('');
                }}
              >
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 14 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {showReportForm && venueData && renderReportFormModal()}
      <View style={globalStyles.overlay}>
        <ScrollView style={{ width: '100%' }}>
          <View style={venueStyles.venueContainer}>
            <TouchableOpacity
              style={venueStyles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={venueStyles.backButtonText}>← Back</Text>
            </TouchableOpacity>

            <View style={[homeStyles.searchContainer, { marginBottom: 30, marginTop: 40 }]}>
              <SearchBar
                value={searchQuery}
                onChangeText={handleSearch}
                onSubmit={handleSearchSubmit}
                placeholder="Search by venue name, location, or affiliates..."
              />
              <TouchableOpacity
                style={venueStyles.locationIconContainer}
                onPress={handleLocationSearch}
                disabled={isGettingLocation}
              >
                {isGettingLocation ? (
                  <ActivityIndicator size="small" color="#000000" />
                ) : (
                  <View style={venueStyles.svgIcon}>
                    <View style={[venueStyles.locationDot, userLocation && { backgroundColor: '#2ecc71' }]} />
                    <View style={[venueStyles.locationRing, userLocation && { borderColor: '#2ecc71' }]} />
                  </View>
                )}
              </TouchableOpacity>
            </View>

            <View style={venueStyles.headerStyle}>
              {renderOverallGrade()}

              <View style={venueStyles.titleCard}>
                <View style={{
                  width: '100%',
                  display: 'flex',
                  marginBottom: 10,
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 20,
                  padding: 0
                }}>
                  {/* <View style={venueStyles.smallerVotingContainer}>
                     <Text style={venueStyles.smallerVotingText}>Is this correct?</Text>
                    
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                   <TouchableOpacity 
                        onPress={() => handleVote('title', 'up')}
                        style={[venueStyles.voteButton, {
                          marginRight: 10,
                          opacity: userTitleVote === 'up' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons 
                          name={userTitleVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'} 
                          size={16} 
                          color="#000000" 
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{titleVotes.up}</Text>
                   </TouchableOpacity>
                      
                      <TouchableOpacity
                        onPress={() => handleVote('title', 'down')}
                        style={[venueStyles.voteButton, {
                          opacity: userTitleVote === 'down' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons 
                          name={userTitleVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'} 
                          size={16} 
                          color="#000000" 
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{titleVotes.down}</Text>
                      </TouchableOpacity>
                    </View>
                  </View> */}
                </View>
                <View style={[{ flex: 1, marginBottom: 'auto' }]}>
                  <View style={venueStyles.venueNameContainer}>
                    <Text style={[venueStyles.venueName, getVenueTextStyle('venueName'), { fontWeight: '500' }]}>{venueData?.name || 'Venue Name'}</Text>
                  </View>

                  <Text style={[venueStyles.venueAddress, getVenueTextStyle('venueAddress')]}>
                    {venueData?.city}, {venueData?.state}
                  </Text>

                  {venueData?.website && (
                    <Text
                      style={[venueStyles.venueWebsite, getVenueTextStyle('website')]}
                      onPress={() => window.open(venueData.website, '_blank')}
                    >
                      {venueData.website}
                    </Text>
                  )}
                  {venueData?.affiliate && (
                    <TouchableOpacity onPress={handleAffiliatePress}>
                      <Text style={[venueStyles.affiliateInfo, getVenueTextStyle('affiliate'), { textAlign: 'center' }]}>
                        {venueData.affiliate}
                      </Text>
                    </TouchableOpacity>
                  )}

                </View>
                {/* {!isMobile && (
                  <View style={[venueStyles.actionButtonsContainer, { marginTop: 0, marginBottom: 0 }]}>
                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        hoveredButton === 'tech' && { backgroundColor: '#434252' }
                     ]}
                      onPress={handleAddTechInfo}
                      onMouseEnter={() => setHoveredButton('tech')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        hoveredButton === 'tech' && { color: '#DBC0FC' }
                      ]}>SHARE VENUE SPECS</Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        (isSaved || hoveredButton === 'save') && { backgroundColor: '#434252' }
                     ]}
                     onPress={handleSaveVenue}
                      onMouseEnter={() => setHoveredButton('save')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        (isSaved || hoveredButton === 'save') && { color: '#DBC0FC' }
                      ]}>
                        {isSaved ? 'VENUE SAVED' : 'SAVE VENUE'}
                      </Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        hoveredButton === 'report' && { backgroundColor: '#434252' }
                     ]}
                      onPress={handleSubmitReport}
                      onMouseEnter={() => setHoveredButton('report')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        hoveredButton === 'report' && { color: '#DBC0FC' }
                      ]}>PROVIDE A REPORT</Text>
                   </TouchableOpacity>
                 </View>
                )} */}
                {isMobile && renderMapCard()}
              </View>
              {!isMobile && renderMapCard()}
            </View>

            <View style={venueStyles.venueLayout}>
              {renderMobileDisplayButtons()}
              {renderActiveSection()}

              {/* {!isMobile && (
                <View style={[venueStyles.rightColumn, (currentView === 'media' || currentView === 'discussion') && { backgroundColor: '#88a0a4' }]}>
                  <View style={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'auto',
                    height: 'auto',
                    overflow: 'visible',
                    ...(Platform.OS === 'web' && {
                      minHeight: getResponsiveValue({
                        mobile: 'auto',
                        tablet: 'auto',
                        laptop: undefined,
                        desktop: undefined
                      })
                    })
                  }}>
                    {currentView === 'discussion' ? (
                      <DiscussionBoard
                        venueId={venueId}
                        messages={messages}
                        loadMessages={loadDiscussionMessages}
                        setMessages={setMessages}
                      />
                    ) : (
                      renderCurrentView()
                    )}
                    <View style={[venueStyles.viewButtonsContainer, { marginTop: 'auto', paddingTop: 20 }]}>
                   <TouchableOpacity 
                     style={[
                          venueStyles.viewButton,
                          currentView === 'media' && { backgroundColor: '#3D7FE6' },
                          hoveredButton === 'media' && venueStyles.viewButtonHovered
                     ]}
                        onPress={() => setCurrentView('media')}
                        onMouseEnter={() => setHoveredButton('media')}
                        onMouseLeave={() => setHoveredButton(null)}
                   >
                        <Text style={[
                          venueStyles.viewButtonText,
                          currentView === 'media' && { color: '#DBC0FC' },
                          hoveredButton === 'media' && venueStyles.viewButtonTextHovered
                        ]}>USER UPLOADS</Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                          venueStyles.viewButton,
                          currentView === 'manuals' && { backgroundColor: '#3D7FE6' },
                          hoveredButton === 'manuals' && venueStyles.viewButtonHovered
                     ]}
                        onPress={() => setCurrentView('manuals')}
                        onMouseEnter={() => setHoveredButton('manuals')}
                        onMouseLeave={() => setHoveredButton(null)}
                   >
                        <Text style={[
                          venueStyles.viewButtonText,
                          currentView === 'manuals' && { color: '#DBC0FC' },
                          hoveredButton === 'manuals' && venueStyles.viewButtonTextHovered
                        ]}>LINKS</Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                          venueStyles.viewButton,
                          currentView === 'discussion' && { backgroundColor: '#3D7FE6' },
                          hoveredButton === 'discussion' && venueStyles.viewButtonHovered
                     ]}
                        onPress={() => setCurrentView('discussion')}
                        onMouseEnter={() => setHoveredButton('discussion')}
                        onMouseLeave={() => setHoveredButton(null)}
                   >
                        <Text style={[
                          venueStyles.viewButtonText,
                          currentView === 'discussion' && { color: '#DBC0FC' },
                          hoveredButton === 'discussion' && venueStyles.viewButtonTextHovered
                        ]}>DISCUSSION BOARD</Text>
                   </TouchableOpacity>
                 </View>
                  </View>
                </View>
              )} */}
            </View>

            {/* Voting Section - Moved outside the layout container */}
            <View style={venueStyles.votingContainer}>
              <Text style={venueStyles.votingText}>Is everything correct?</Text>

              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TouchableOpacity
                  onPress={() => handleVote('venue', 'up')}
                  style={[venueStyles.voteButton, {
                    marginRight: 15,
                    opacity: userVote === 'up' ? 1 : 0.7,
                    flexDirection: 'row',
                    alignItems: 'center'
                  }]}
                >
                  <Ionicons
                    name={userVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
                    size={24}
                    color="#000000"
                  />
                  <Text style={venueStyles.voteCount}>{votes.up}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => handleVote('venue', 'down')}
                  style={[venueStyles.voteButton, {
                    opacity: userVote === 'down' ? 1 : 0.7,
                    flexDirection: 'row',
                    alignItems: 'center'
                  }]}
                >
                  <Ionicons
                    name={userVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
                    size={24}
                    color="#000000"
                  />
                  <Text style={venueStyles.voteCount}>{votes.down}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </View>

  );
}

